/**
 * Automatic offline preload.
 *
 * The band's data (rider, config, musicians, crew, stageplots, setlists,
 * checklists, itinerary — plus the shared team/shows/venues/contacts/calendar)
 * is fetched through the normal apiGet* layer, which caches every successful
 * GET into the IndexedDB offline cache automatically (see src/api.ts
 * realFetchJSON → cachePut). So "preloading" is just firing those fetches in
 * the background — the offline cache fills itself and no manual button is
 * needed anymore.
 *
 * Everything here is fire-and-forget: callers never await it, so it can never
 * slow down login or band load. Jobs run sequentially (as the old manual
 * preload page did) to avoid hammering the server, and each band is fetched at
 * most once per session.
 */
import {
  apiGetRider,
  apiGetConfig,
  apiGetMusicians,
  apiGetCrew,
  apiGetStagePlots,
  apiGetSetlists,
  apiGetChecklists,
  apiGetItinerary,
  apiGetTeam,
  apiGetShows,
  apiGetVenues,
  apiGetContacts,
  apiGetCalendar,
} from '../api';

const doneBands = new Set<string>();
let globalDone = false;

/** Fetch the shared (non-band-scoped) data into the offline cache, once. */
async function preloadGlobal(): Promise<void> {
  if (globalDone) return;
  globalDone = true;
  const jobs = [apiGetTeam, apiGetShows, apiGetVenues, apiGetContacts, apiGetCalendar];
  for (const fn of jobs) {
    try { await fn(); } catch { /* offline / failure — cache stays as-is */ }
  }
}

/** Fetch one band's data into the offline cache, once per session. */
async function preloadBand(bandId: string): Promise<void> {
  if (!bandId || doneBands.has(bandId)) return;
  doneBands.add(bandId);
  const jobs = [
    apiGetRider, apiGetConfig, apiGetMusicians, apiGetCrew,
    apiGetStagePlots, apiGetSetlists, apiGetChecklists, apiGetItinerary,
  ];
  for (const fn of jobs) {
    try { await fn(bandId); } catch { /* offline / failure — cache stays as-is */ }
  }
}

/** Fire-and-forget preload for one band (no-op if already done). */
export function autoPreloadBand(bandId: string): void {
  if (!bandId || doneBands.has(bandId)) return;
  // Do not await — non-blocking by design.
  void preloadBand(bandId);
}

/** Fire-and-forget preload for every band the user can access + shared data. */
export function autoPreloadAll(bandIds: string[]): void {
  if (!globalDone) void preloadGlobal();
  for (const id of bandIds) autoPreloadBand(id);
}
